import { ContentContainer } from "components/ui/display/ContentContainer";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Tag } from "components/ui/display/Tag";
import { parseDeadline } from "utilities/dateAndTime";
import { MaterialIcon } from "components/ui/shared/MaterialIcon";

export const ImportansTasksContainer = ({tasks, projects}) => {

    const sortedTasks = sortTasks(tasks);

    return (
        <StyledImportansTasksContainer padding='none'>
            <div className="importantTasksHeader">
                <MaterialIcon name='ballot' weight={300} size='sm'/> Viktige oppgaver <div/>
            </div>
            <div className="importantTasksContent">
                {
                    sortedTasks.map(tsk => <TaskThumbnail key={tsk.taskUuid} task={tsk} project={projects.find(prj => prj.projectUuid === tsk.taskProjectUuid)}/>)
                }
            </div>
        </StyledImportansTasksContainer>
    )
}

const StyledImportansTasksContainer = styled(ContentContainer)`

    overflow: hidden;
    min-height: 400px;
    max-height: 500px;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        flex: 1 0 0%;

    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        width: 100%;        
    }

    div.importantTasksHeader
    {
        padding: ${({theme}) => theme.spacing.md};
        border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
        display: flex;
        align-items: center;
        gap: ${({theme}) => theme.spacing.md};
        font-weight: 600;

        .icon
        {
            color: ${({theme}) => theme.colors.mutedForeground};
        }

        > div
        {
            height: 32px;
        }
    }

    div.importantTasksContent
    {
        padding: ${({theme}) => theme.spacing.md};
        overflow: auto;
        flex: 1;
    }
`

const TaskThumbnail = ({task, project}) => {

    const navigate = useNavigate();

    if(!task || !project) return null;

    let today = new Date();
    today.setHours(0, 0, 0, 0);
    const isOverdue = task.taskDeadline < today;

    return (
        <StyledTaskThumbnail
            onDoubleClick={() => navigate('/tasks/' + task.taskUuid)}
            className={isOverdue ? 'overdueTask' : null}
        >
            <div className="taskThumbnailContent">
                <div className='taskTitle'>
                    {task.taskTitle}
                </div>
                <div className="projectName">
                    {project.projectName}
                    <div className="dot"/>
                    <MaterialIcon
                        name='calendar_today'
                        weight={300}
                        size="sm"
                        color={({theme}) => theme.colors.mutedForeground}
                    />
                    <span>{isOverdue ? "Forfalt" : parseDeadline(task.taskDeadline)}</span>
                </div>

            </div>
            <div className="taskThumnailTags">
                <Tag
                    icon={<MaterialIcon name='flag' weight={400}/>}
                    type={task.taskPriority === 3 ? 'error' : task.taskPriority === 2 ? 'warning' : 'success'}
                />
                <MaterialIcon
                    name="arrow_forward"
                    weight={300}
                    size='md'
                    color={({theme}) => theme.colors.mutedForeground}
                    onClick={() => navigate('/tasks/' + task.taskUuid)}
                />
            </div>
        </StyledTaskThumbnail>
    )
}

const StyledTaskThumbnail = styled.div`
    border-bottom: 1px solid ${({theme}) => theme.colors.neutralPrimaryBorder};
    padding: ${({theme}) => theme.spacing.md} ${({theme}) => theme.spacing.sm};
    
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;

    &:hover
    {
        background: ${({theme}) => theme.colors.neutralSecondaryBackground};
        border-radius: 10px;
    }

    &.overdueTask
    {
        div.taskThumbnailContent div.projectName span, div.taskThumbnailContent div.projectName svg
        {
            color: ${({theme}) => theme.colors.errorForeground};
            color: ${({theme}) => theme.colors.errorForeground};
        }
    }

    div.taskThumbnailContent
    {
        display: flex;
        flex-direction: column;
        gap: ${({theme}) => theme.spacing.md};
        padding-left: ${({theme}) => theme.spacing.xl};

        div.taskTitle
        {
            font-weight: 500;
            position: relative;

            &:before
            {
                content: "";
                height: 12px;
                width: 12px;
                position: absolute;
                top: 50%;
                left: -${({theme}) => theme.spacing.md};
                transform: translateX(-100%) translateY(-50%);
                border-radius: 999px;
                background: ${({theme}) => theme.colors.neutralPrimaryForeground};
            }
        }

        div.projectName
        {
            color: ${({theme}) => theme.colors.mutedForeground};
            font-size: ${({theme}) => theme.fontSize.sm};
            display: flex;
            gap: ${({theme}) => theme.spacing.sm};
            align-items: center;

            div.dot
            {
                height: 6px;
                width: 6px;
                background: ${({theme}) => theme.colors.mutedForeground};
                border-radius: 999px;
            }
        }
    }

    div.taskThumnailTags
    {
        display: flex;
        align-items: center;
        gap: ${({theme}) => theme.spacing.md};

    }
`

const STATUS_WEIGHT = {
  Stopped: 3,
  Started: 2,
  NotStarted: 1,
  Finished: 0,
};

const sortTasks = (tasks) => {
  const now = new Date();

  return tasks
    .filter(task => task.taskStatus !== "Finished" && task.taskStart <= now)
    .sort((a, b) => {
        const aOverdue = a.taskDeadline < now;
        const bOverdue = b.taskDeadline < now;

        if(aOverdue !== bOverdue) return aOverdue ? -1 : 1;
        const deadlineDiff = a.taskDeadline.getTime() - b.taskDeadline.getTime();

        if(deadlineDiff !== 0) return deadlineDiff;

        if(a.taskPriority !== b.taskPriority) return b.taskPriority - a.taskPriority;

        const statusDiff =  STATUS_WEIGHT[b.taskStatus] - STATUS_WEIGHT[a.taskStatus];

        if(statusDiff !== 0) return statusDiff;

        return 0;
    });
}